import type { Tour } from '@/config/objects/tours';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  EmptyTableState,
  Input,
  Skeleton,
  useApiClient,
} from '@startupquickstart/frontend';
import { useQuery } from '@tanstack/react-query';
import { Ban, Map, Search, SearchX } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

interface LoopTourSelectorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  hotelId: string;
  greaterAreaId?: string;
  /** Optional variant label (e.g. "Short" / "Long") shown in the dialog title. */
  variantLabel?: string;
  /**
   * Extra tour ids to mark ineligible in addition to the hotel's regular linked
   * tours — e.g. the hotel's existing short/long loop tours. The backend rejects
   * assigning a tour that is already the other loop variant for this hotel;
   * greying them out here keeps the picker from offering a choice that will fail.
   */
  excludeTourIds?: string[];
  onSelect: (tourId: string) => void;
  isLoading?: boolean;
}

interface ToursResponse {
  success: boolean;
  data: Tour[];
  pagination: { total: number };
}

/** Debounce hook for search input */
function useDebounce(value: string, delayMs: number): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}

/** Skeleton rows shown while loading */
function TourListSkeleton() {
  return (
    <div className="space-y-2">
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="flex items-start gap-3 rounded-lg border p-3">
          <Skeleton className="h-9 w-9 shrink-0 rounded-md" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-3 w-1/2" />
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Dialog for selecting a tour to assign as the hotel's loop tour.
 * Uses the standard `search` query param for fuzzy matching across
 * tour name, description, and tagline fields.
 */
export function LoopTourSelector({
  open,
  onOpenChange,
  hotelId,
  greaterAreaId,
  variantLabel,
  excludeTourIds,
  onSelect,
  isLoading,
}: LoopTourSelectorProps) {
  const apiClient = useApiClient();
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);

  // Reset search when dialog opens
  useEffect(() => {
    if (open) setSearch('');
  }, [open]);

  const { data, isLoading: isLoadingTours } = useQuery<ToursResponse>({
    queryKey: ['tours-for-loop-tour', hotelId, greaterAreaId, debouncedSearch],
    queryFn: () =>
      apiClient.get('/tours', {
        params: {
          limit: 50,
          ...(greaterAreaId && { 'where[greaterAreaId]': greaterAreaId }),
          ...(debouncedSearch.trim() && { search: debouncedSearch.trim() }),
        },
      }),
    enabled: open,
  });

  // Fetch tours already assigned to this hotel to mark them as ineligible
  const { data: hotelToursData } = useQuery<ToursResponse>({
    queryKey: ['hotel-tours', hotelId],
    queryFn: () => apiClient.get(`/hotels/${hotelId}/tours`, { params: { limit: 200 } }),
    enabled: open,
  });

  // Tours linked to this hotel via the regular HotelTour junction.
  const linkedTourIds = useMemo(() => {
    const ids = new Set<string>();
    hotelToursData?.data?.forEach((t) => ids.add(t.id));
    return ids;
  }, [hotelToursData]);

  // The hotel's existing loop tours (short/long) — ineligible for the same
  // reasons but for a different reason than a regular link, so we badge them
  // distinctly below.
  const loopTourIdSet = useMemo(
    () => new Set(excludeTourIds ?? []),
    [excludeTourIds],
  );

  const tours = data?.data ?? [];
  const hasSearch = debouncedSearch.trim().length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            Assign {variantLabel ? `${variantLabel} ` : ''}Loop Tour
          </DialogTitle>
          <DialogDescription>
            Select a tour to designate as this hotel&apos;s
            {variantLabel ? ` ${variantLabel.toLowerCase()}` : ''} loop tour. The
            tour must not be assigned to other hotels. Its path mode will be set
            to &quot;defined&quot;.
          </DialogDescription>
        </DialogHeader>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search by name, description, or tagline..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>

        <div className="max-h-[400px] min-h-[200px] overflow-y-auto -mx-1 px-1">
          {isLoadingTours ? (
            <TourListSkeleton />
          ) : tours.length === 0 ? (
            hasSearch ? (
              <EmptyTableState
                icon={<SearchX className="h-6 w-6" />}
                title="No matching tours"
                description={`No tours found matching "${debouncedSearch}". Try a different search term.`}
                action={{
                  label: 'Clear search',
                  onClick: () => setSearch(''),
                  variant: 'outline',
                }}
              />
            ) : (
              <EmptyTableState
                icon={<Map className="h-6 w-6" />}
                title="No tours available"
                description="There are no tours in this hotel's area. Create a tour first, then assign it as the loop tour."
              />
            )
          ) : (
            <div className="space-y-2">
              {tours.map((tour) => {
                const isLoopTour = loopTourIdSet.has(tour.id);
                const isAssigned = isLoopTour || linkedTourIds.has(tour.id);
                const ineligibleLabel = isLoopTour
                  ? 'Loop tour'
                  : 'Assigned to hotel';
                return (
                  <button
                    key={tour.id}
                    type="button"
                    onClick={() => !isAssigned && onSelect(tour.id)}
                    disabled={isLoading || isAssigned}
                    className={`w-full rounded-lg border p-3 text-left transition-colors focus:outline-none focus:ring-2 focus:ring-ring ${
                      isAssigned
                        ? 'cursor-not-allowed border-dashed bg-muted/30 opacity-60'
                        : 'hover:bg-accent'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-md ${
                        isAssigned ? 'bg-muted/50' : 'bg-muted'
                      }`}>
                        {isAssigned ? (
                          <Ban className="h-4 w-4 text-muted-foreground/50" />
                        ) : (
                          <Map className="h-4 w-4 text-muted-foreground" />
                        )}
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className={`font-medium text-sm ${isAssigned ? 'text-muted-foreground' : ''}`}>
                            {tour.name}
                          </span>
                          {isAssigned && (
                            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-700">
                              {ineligibleLabel}
                            </span>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-2 mt-1 text-xs text-muted-foreground">
                          {tour.stops != null && <span>{tour.stops} stops</span>}
                          {tour.duration && <span>{tour.duration} min</span>}
                          {tour.difficulty && <span>{tour.difficulty}</span>}
                          {tour.tourType && (
                            <span className="rounded-full bg-muted px-2 py-0.5">
                              {tour.tourType.name}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex justify-end">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
